/** One server-sent event: `id:` (only if its block had one), `event:` and `data:`. */
export type SseFrame = { id?: string; event: string; data: string };

/**
 * Incremental text/event-stream parser. Push decoded text in any chunk sizes;
 * onFrame runs once per complete event block. The agent service (FastAPI's
 * `fastapi.sse`) sends LF line endings and `: ping` keep-alive comments; CRLF is
 * accepted too, as the SSE spec allows it. Handles `:` comment lines and
 * multi-line data. `retry:` is ignored: reconnect timing is the client's.
 */
export function createSseParser(onFrame: (frame: SseFrame) => void) {
  let buffer = '';
  let id: string | undefined;
  let event = '';
  let data: string[] = [];

  const dispatch = () => {
    if (data.length > 0) {
      const frame: SseFrame = {
        event: event || 'message',
        data: data.join('\n'),
      };
      if (id !== undefined) frame.id = id;
      onFrame(frame);
    }
    id = undefined;
    event = '';
    data = [];
  };

  const processLine = (raw: string) => {
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') data.push(value);
    else if (field === 'event') event = value;
    else if (field === 'id') id = value;
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let newline = buffer.indexOf('\n');
      while (newline !== -1) {
        processLine(buffer.slice(0, newline));
        buffer = buffer.slice(newline + 1);
        newline = buffer.indexOf('\n');
      }
    },
  };
}
